import { useMutation } from "@tanstack/react-query";
import { UserProfileDto, UserRole } from "@green0meter/api-client";

import { DOMAIN_FORMS } from "../domain";

import { formsConfigApi } from "@api/services";
import { roleGuard } from "@api";
import { triggerDownloadFile } from "@helpers";

export const ACTION_EXPORT_FORM_CONFIG = "EXPORT_FORM_CONFIG";

const allowedRoles = [UserRole.Owner, UserRole.Manager, UserRole.Respondent];

type ExportFormConfigRequest = {
  formId: string;
  filename: string;
};

const exportFormConfig = async ({
  formId,
  filename,
}: ExportFormConfigRequest): Promise<void> => {
  const response = await formsConfigApi.formsConfigControllerExportFormConfigJson(
    formId,
    {
      responseType: "blob",
    },
  );

  // Ensure the blob has explicit UTF-8 charset to prevent encoding issues
  const blob = new Blob([response.data], {
    type: "application/json; charset=utf-8"
  });

  return triggerDownloadFile(blob, filename);
};

export const useExportFormConfig = (
  user: UserProfileDto | null,
): {
  exportFormConfig: (req: ExportFormConfigRequest) => Promise<void>;
  exportFormConfigEnabled: boolean;
  isSuccessExportFormConfig: boolean;
  isPendingExportFormConfig: boolean;
  isErrorExportFormConfig: boolean;
} => {
  const enabled = roleGuard(user, allowedRoles);

  const { mutateAsync, isSuccess, isPending, isError } = useMutation({
    mutationKey: [DOMAIN_FORMS, ACTION_EXPORT_FORM_CONFIG],
    mutationFn: exportFormConfig,
  });

  return {
    exportFormConfig: mutateAsync,
    exportFormConfigEnabled: enabled,
    isSuccessExportFormConfig: isSuccess,
    isPendingExportFormConfig: isPending,
    isErrorExportFormConfig: isError,
  };
};
